"use client";

import React, { useEffect, useRef } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination, Autoplay } from "swiper/modules";
import { FaQuoteLeft, FaStar } from "react-icons/fa";
import { MdRateReview } from "react-icons/md";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/dist/ScrollTrigger";
import Image from "next/image";

// Import Swiper styles
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

// Create a CSS file for this component
export function Review() {
  const sectionRef = useRef(null);
  const headingRef = useRef(null);

  useEffect(() => {
    gsap.registerPlugin(ScrollTrigger);

    const ctx = gsap.context(() => {
      // Animate the heading
      gsap.fromTo(headingRef.current,
        { y: 30, opacity: 0 },
        {
          scrollTrigger: {
            trigger: headingRef.current,
            start: "top 80%",
            end: "bottom 20%",
            toggleActions: "play none none reverse"
          },
          y: 0,
          opacity: 1,
          duration: 0.8,
          ease: "power3.out"
        }
      );
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section ref={sectionRef} className="testimonial-section py-16 relative overflow-hidden" style={{
      background: "linear-gradient(to bottom, #ffffff, #f0f7ff, #ffffff)"
    }}>
      {/* Background pattern */}
      <div className="absolute inset-0 bg-pattern opacity-5"></div>
      
      {/* Decorative Blobs */}
      <div className="absolute top-[10%] right-[5%] w-[300px] h-[300px] rounded-full bg-gradient-to-r from-blue-500 to-blue-400 filter blur-[50px] opacity-10"></div>
      <div className="absolute bottom-[10%] left-[5%] w-[250px] h-[250px] rounded-full bg-gradient-to-r from-yellow-400 to-yellow-500 filter blur-[50px] opacity-10"></div>
      
      <div className="container mx-auto px-4 md:px-8 max-w-7xl relative z-10">
        {/* Section Header */}
        <div ref={headingRef} className="text-center mb-16">
          <div className="flex items-center justify-center mb-4">
            <div className="h-[1px] w-12 bg-blue-600"></div>
            <span className="mx-4 text-blue-600 font-semibold text-sm uppercase tracking-wider">Success Stories</span>
            <div className="h-[1px] w-12 bg-blue-600"></div>
          </div>
          
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 flex items-center justify-center">
            <MdRateReview className="text-blue-600 mr-3" />
            What Our Students Say
          </h2>
          
          <p className="text-gray-600 max-w-2xl mx-auto">
            Hear from our students who have transformed their careers with our courses
          </p>
        </div>

        {/* Testimonial Slider */}
        <div className="testimonial-swiper">
          <Swiper
            breakpoints={{
              340: {
                slidesPerView: 1,
                spaceBetween: 20,
              },
              640: {
                slidesPerView: 2,
                spaceBetween: 30,
              },
              1024: {
                slidesPerView: 3,
                spaceBetween: 40,
              },
            }}
            navigation={true}
            pagination={{
              clickable: true,
              dynamicBullets: true,
            }}
            autoplay={{
              delay: 3000,
              disableOnInteraction: false,
              pauseOnMouseEnter: true,
            }}
            modules={[Navigation, Pagination, Autoplay]}
            className="max-w-full px-4 pb-16"
          >
            {testimonials.map((testimonial, idx) => (
              <SwiperSlide key={`${testimonial.name}-${idx}`} className="h-auto flex">
                <div className="testimonial-card bg-white rounded-xl overflow-hidden shadow-lg p-6 flex flex-col w-full h-full">
                  <div className="flex flex-col items-center mb-6">
                    {/* Student Image */}
                    <div className="relative mb-4">
                      <div className="absolute inset-0 rounded-full bg-gradient-to-r from-blue-500 to-blue-600 blur-sm opacity-30 scale-110"></div>
                      <Image 
                        src={testimonial.imgurl} 
                        alt={testimonial.name} 
                        width={120}
                        height={120}
                        className="testimonial-image w-24 h-24 md:w-32 md:h-32 rounded-full object-cover relative z-10" 
                      />
                    </div>
                    
                    {/* Student Name & Role */}
                    <h3 className="text-lg font-bold text-gray-800 text-center">{testimonial.name}</h3>
                    {testimonial.title && (
                      <p className="text-blue-600 text-sm font-medium text-center">{testimonial.title}</p>
                    )}
                    
                    {/* Rating Stars */}
                    <div className="flex items-center mt-2 text-yellow-400">
                      <FaStar />
                      <FaStar />
                      <FaStar />
                      <FaStar />
                      <FaStar />
                    </div>
                  </div>
                  
                  {/* Testimonial Quote */}
                  <div className="flex-grow mb-6 relative">
                    <FaQuoteLeft className="text-blue-100 text-4xl absolute -top-2 -left-1 quote-icon transition-transform duration-300" />
                    <p className="text-gray-600 text-sm leading-relaxed relative z-10 pl-4 line-clamp-6">
                      {testimonial.quote}
                    </p>
                  </div>
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      </div>

      <style jsx>{`
        .testimonial-section::before {
          content: "";
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background-image: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%233b82f6' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
          opacity: 0.4;
          z-index: 0;
        }
        
        .testimonial-swiper .swiper-button-next,
        .testimonial-swiper .swiper-button-prev {
          color: #3b82f6;
          background: rgba(255, 255, 255, 0.9);
          width: 40px;
          height: 40px;
          border-radius: 50%;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
          transition: all 0.3s ease;
        }

        .testimonial-swiper .swiper-button-next:after,
        .testimonial-swiper .swiper-button-prev:after {
          font-size: 18px;
          font-weight: bold;
        }

        .testimonial-swiper .swiper-button-next:hover,
        .testimonial-swiper .swiper-button-prev:hover {
          background: #3b82f6;
          color: white;
          transform: scale(1.1);
        }

        .testimonial-swiper .swiper-pagination-bullet {
          width: 10px;
          height: 10px;
          background: #3b82f6;
          opacity: 0.5;
          transition: all 0.3s ease;
        }

        .testimonial-swiper .swiper-pagination-bullet-active {
          opacity: 1;
          width: 20px;
          border-radius: 5px;
        }

        .testimonial-card {
          transition: all 0.3s ease;
          transform: translateY(0);
          height: 100%;
          display: flex;
          flex-direction: column;
          min-height: 450px;
        }

        .testimonial-card:hover {
          transform: translateY(-10px);
          box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
        }

        .testimonial-image {
          transition: all 0.3s ease;
          border: 3px solid transparent;
        }

        .testimonial-card:hover .testimonial-image {
          border-color: #3b82f6;
          transform: scale(1.05);
        }

        .testimonial-card:hover .quote-icon {
          transform: rotate(-10deg);
        }

        .line-clamp-6 {
          display: -webkit-box;
          -webkit-line-clamp: 6;
          -webkit-box-orient: vertical;
          overflow: hidden;
          max-height: 144px;
        }

        @media (max-width: 768px) {
          .testimonial-swiper .swiper-button-next,
          .testimonial-swiper .swiper-button-prev {
            display: none;
          }
          
          .testimonial-card {
            max-width: 320px;
            margin: 0 auto;
            min-height: 400px;
          }
          
          .testimonial-image {
            width: 100px;
            height: 100px;
          }
        }
      `}</style>
    </section>
  );
}

const testimonials = [
  {
    quote:
      "Nirmalyada eto easily python ta sikhiyechile bolei amar ekhon basic concept ta clear hye geche..ar Java sikhte osubidha hocchena ekdom e..plus recording gulo o diyechile..jokhon e kono osubidha hoy kono topic e,sei video gulo dekhe ni.",
    name: "Riya Mondal",
    title: "Python Student",
    imgurl: "/images/reviewer4.png",
  },
  {
    quote:
      "Content writing er class kore amr vison valo legeche. Puro bisoy ta Bangla e bojhanor jonno aro vlo jeno bujhte subidhe hoeche. Sayani ma'am ashadharon vabe bojhan ...onar class korte khub e valo lage.recently onar e internship er team e join korechi and kaj o sikhchi.  ❤️❤️",
    name: "Shatabdi Banerjee",
    title: "Content Writing Student",
    imgurl: "/images/reviewer10.jpg",
  },
  {
    quote: "Tushar Sir's class on Full Stack Web Development has been incredibly beneficial for me. He enhanced my skills and confidence in Full Stack Web Development.",
    name: "Ipsita Mukherjee",
    title: "Web Development Student",
    imgurl: "/images/reviewer7.png",
  },
  {
    quote:
      "এক দাদার কাছে vidyastur ব্যাপারে জানতে পেরে নির্মাল্যদাকে ফোন করেছিলাম বড্ড puzzle ছিলাম কোর্সটা করার আগে। ফরিদ স্যার খুবই ভালো করে সবটা বুঝিয়ে ক্লাস করাতেন লাস্ট ক্লাস শেষের পরদিনই জব হয়ে গেলো...আগে জানিনা কি হবে না হবে তবে thanks to nirmalya da❤️",
    name: "Sudipto Ghosh",
    title: "MERN Stack Student",
    imgurl: "/images/reviewer5.png",
  },
  {
    quote:
      "I joined vidyastu to take training for MERN stack . My teacher Farid sir is highly skilled and a great teacher. I got a job at Msqube technology solutions pvt ltd.",
    name: "Anirban Mukherjee",
    title: "MSQube Technology Solutions Pvt Ltd",
    imgurl: "/images/reviewer6.png"
  },
  {
    quote:
      "I decided to enroll at Fullstack Development after a few classes my experience was relatively good, the faculty teachers were very knowledgeable, the entire @vidyastu community is very helpful and create a great ambiance to learn thanks Vidyastu",
    name: "Dwipen Dutta",
    title: "Full Stack Development Student",
    imgurl: "/images/reviewer8.jpg"
  },
  {
    quote:
      "I joined the digital marketing course and had a fantastic learning experience. The offline batch offered hands-on experience with real-world scenarios. I am  particularly grateful to Kaushik Sir for his guidance and support during the training. I was hired as an SEO Analyst at Archtech Design. I strongly recommend Vidyastu .",
    name: "Sourav Naskar",
    title: "Archtech Design",
    imgurl: "/images/reviewer9.jpeg"
  },
  {
    quote:
      "Vidyastu has helped me a lot to understand the core of digital marketing. Nilanjan sir was extremely helpful and help us with patience. He enlightened us the details of interview process that has helped me to get my job in digital marketing. Thank you entire vidyastu team to guide us.",
    name: "Nivedita Ghosh",
    title: "SEO Analyst at ZTS infotech Pvt Ltd",
    imgurl: "/images/reviewer20.jpeg"
  },
  {
    quote:
      "আমার নাম তানিসা মজুমদার। আমি একজন কলেজ পড়ুয়া ছাত্রী, ভূগোল অনার্স, দ্বীতিয় বৎসর চলছে। এটার সাথে বিদ্যাস্তুর কনটেন্ট রাইটিং কোর্সটি আমি করেছি, এবং সেখানে সায়নী ম্যাম এর ক্লাস ভীষন ভালো। যথেষ্ট যত্ন করে ক্লাস করাতেন এবং কোনো বিষয় বোঝানোর ক্ষেত্রে খুব সহজ করে বুঝিয়ে দিতেন। আর নিউজ কনটেন্ট রাইটিং আমাদের প্রিয়ম স্যার করিয়েছেন তিনিও সুন্দর ধরে ধরে বিষয় গুলো শিখিয়েছেন। ধন্যবাদ টিম বিদ্যাস্তুকে 🙏",
    name: "Tanisha Majumder",
    title: "Content Writing Student",
    imgurl: "/images/reviewer21.jpeg"
  },
  {
    quote:
      "অ্যাডমিশন কনসালটেন্ট পৃথা ম্যাডাম কাউন্সেলিং ও সঠিক গাইডেন্সের মাধ্যমে আমাকে মোটিভেট করেন ফলে আমি কনটেন্ট রাইটিং কোর্সে ভর্তি হই। ক্লাসের প্রথম দিন থেকেই আমাদের এডুকেটর সায়নী ম্যাডাম ও প্রিয়ম স্যার এতো যত্ন নিয়ে ক্লাস করাতেন ও আমাদের উৎসাহিত করতেন যে ক্লাস করার আগ্রহ অনেকগুন বেড়ে যেত। আর যে অপরিসীম ধৈর্য্য নিয়ে বারবার আমাদের ডাউট ক্লিয়ার করে দিতেন, অ্যাসেসমেন্ট চেক করে ভুল-ত্রুটি সংশোধন করে দিতেন তারজন্য সত্যিই অনেক কিছু শিখতে পেরেছি। বিদ্যাস্তুর স্যার ম্যাডামদের জন্যই আজ আমি কনটেন্ট লিখে উপার্জন করতে সক্ষম। অসংখ্য ধন্যবাদ বিদ্যাস্তুকে",
    name: "Ramyani Sen Roy",
    title: "Content Writer",
    imgurl: "/images/reviewer22.jpeg"
  },
];
